import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  FinancialProjectForSelectFragment,
  FinancialProjectSelectorDocument,
  FinancialProjectSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

import { SubprojectSelector } from 'modules/project_management/components/selectors/SubprojectSelector'

import {
  FinancialProjectSelectorMultipleProps,
  FinancialProjectSelectorSingleWithSubprojectsProps,
  FinancialProjectSelectorSingleWithoutSubprojectsProps,
} from './types'

const FinancialProjectItem = ({ project }: { project: FinancialProjectForSelectFragment }) => {
  return (
    <Box gap="s2">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Text.Normal weight="medium" color="grey1000">
          {project.name}
        </Text.Normal>
      </Box>
      {project.code && (
        <Box flexDirection="row">
          <Text.Normal size="200">{project.code}</Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export const FinancialProjectSelector: React.FC<
  | FinancialProjectSelectorSingleWithSubprojectsProps
  | FinancialProjectSelectorSingleWithoutSubprojectsProps
  | FinancialProjectSelectorMultipleProps
> = (props) => {
  const { value, multiple, billable, hideSearchIcon } = props

  const variables = {
    billable,
  }

  const extractConnection = (data: FinancialProjectSelectorQuery) =>
    data.projectManagement.financialProjectsConnection

  const renderEntity = (entity: FinancialProjectForSelectFragment) => (
    <FinancialProjectItem project={entity} />
  )

  // We need Typescript to evaluate this variable and to be sure that will not change, so later on,
  // we can use it to check if we should render the SubprojectSelector component and safely access other props
  const withSubprojects =
    !multiple && props.subprojectValue !== undefined && !!props.onChangeSubproject

  const commonProps = {
    document: FinancialProjectSelectorDocument,
    extractConnection,
    label: i18n.t('project_management.modals.add_project.select_project'),
    renderEntity,
    variables,
    disabled: props.projectDisabled,
  }

  if (multiple) {
    return (
      <EntitySelector.Multiple
        {...commonProps}
        {...props}
        renderValue={(financialProjects) => (
          <Text.Normal ellipsis>
            {financialProjects.length > 0
              ? financialProjects.map((financialProject) => financialProject.name).join(', ')
              : i18n.t('common.select_multiple')}
          </Text.Normal>
        )}
      />
    )
  }

  return (
    <>
      <EntitySelector
        {...commonProps}
        {...props}
        renderValue={(entity) =>
          entity !== null && entity != undefined ? (
            <Text.Normal ellipsis>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal>{i18n.t('common.select')}</Text.Normal>
          )
        }
      />
      {withSubprojects && value && (value?.subprojectsConnection?.totalCount || 0 > 0) && (
        <SubprojectSelector
          financialProjectId={value.id}
          value={props.subprojectValue}
          onChange={props.onChangeSubproject}
          disabled={props.subprojectDisabled}
          hideSearchIcon={hideSearchIcon}
          onlyActive={props.subprojectOnlyActive}
          notClosed={props.subprojectNotClosed}
        />
      )}
    </>
  )
}
